import videojs from 'video.js';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

const _isOnMobile = videojs.browser.IS_IOS || videojs.browser.IS_NATIVE_ANDROID;
const Tech = videojs.getTech('Tech');

let hasUserInteraction = false;

class Iframe extends Tech {
  /**
    * The constructor for the iframe tech. Applies options to the "class" and begins loading the iframe if a src is provided.
    *
    * @param {Object} options - An object that contains the initialization parameters. Currently this is only `debug` (Boolean).
    * @param {Function} ready - A ready function passed in by VideoJS.
    *
    */
  constructor(options = {}, ready) {
    super(options, ready);
    this.uuid = options.uuid || uuidv4();
    this.debugPrefix = options.debugPrefix || this.uuid;
    this.debug = options.debug;
    this.pqOrigin = options.pqOrigin || 'https://master.ui.nuvo.alexanderstreet.com';
    this.proquestVideoUrl = options.proquestVideoUrl;
    this.ppWrapper = options.ppWrapper;
    this.queuedEvents = [];
    this.fullscreen = true;
    this.log('constructor()');
    this.setPoster(this.options_.poster);
    if (this.options_.source) this.setSrc(this.options_.source);
    this.setTimeout(function initializeChildPlayerTimeout() {
      if (this.el_) {
        this.el_.parentNode.className += ' vjs-iframe';
        if (_isOnMobile) this.el_.parentNode.className += ' vjs-iframe-mobile';
        this.triggerReady();
      }
    }.bind(this));
  }

  /**
    * A function that logs a color-coded message to the console if `debug` is `true`.
    *
    * @param {Object} args - A destructred array of all arguments passed into the `log` function which are then logged out in that same order.
    *
    */
  log(...args) {
    /* eslint no-console: "off" */
    if (this.debug) console.log(`%c[${this.debugPrefix}]`, 'color: white; background: #2196F3', ...args);
  }

  /**
    * A function that creates the wrapper element that the Iframe tech resides within.
    */
  createEl() {
    const div = document.createElement('div');
    div.setAttribute('id', this.options_.techId);
    Object.assign(div.style, {
      position: 'absolute',
      height: '100%',
      width: '100%',
      top: 0,
      left: 0,
    });
    div.setAttribute('class', 'vjs-tech');
    return div;
  }

  /**
    * An asynchronous function that adds an iframe element with a Proquest video source into the DOM. This function only resolves after we have both (1) receieved the "onload" event from the iframe and (2) received the "ready" event from the VideoJS instance inside the iframe.
    *
    */
  async loadIframe(src) {
    return new Promise((resolve, reject) => {
      this.log('Starting iframe initialization...');
      if (!src) {
        reject(false);
        return;
      }
      const preexistingIframeElement = this.el_.querySelector(`#videojs-child-player-iframe-${this.uuid}`);
      if (preexistingIframeElement) {
        this.childPlayerIframe = preexistingIframeElement;
        resolve(true);
        return;
      }
      const iframeElement = document.createElement('iframe');
      iframeElement.id = `videojs-child-player-iframe-${this.uuid}`;
      iframeElement.classList.add('videojs-child-player-iframe');
      Object.assign(iframeElement.style, {
        height: '100%',
        width: '100%',
      });
      iframeElement.setAttribute('frameBorder', '0');
      iframeElement.setAttribute('allow', 'autoplay *; encrypted-media *');
      this.el_.appendChild(iframeElement);
      let receivedLoadingEventCount = 0;
      const listenForChildPlayerReadyEvent = (event) => {
        let eventData;
        try {
          eventData = JSON.parse(event?.data);
        } catch(e) {
          return;
        }
        if (
          eventData?.source !== 'playpositChildPlayer'
          || eventData.uuid !== this.uuid
        ) return;
        if (event.source) this.postMessageSource = event.source;
        if (event.origin) this.postMessageOrigin = event.origin;
        if (
          !this.hasLoadedIframe
          && eventData.type === 'ready'
        ) {
          window.removeEventListener('message', listenForChildPlayerReadyEvent);
          receivedLoadingEventCount += 1;
          this.log('Received ready event!', receivedLoadingEventCount, eventData.uuid);
          if (receivedLoadingEventCount === 2) {
            this.handleLoadedIframe();
            resolve(true);
          }
        } else if (eventData.type === 'error') {
          window.removeEventListener('message', listenForChildPlayerReadyEvent);
          reject(false);
        }
      };
      iframeElement.onload = () => {
        receivedLoadingEventCount += 1;
        this.log('Received onload event!', receivedLoadingEventCount);
        if (receivedLoadingEventCount === 2) {
          this.handleLoadedIframe();
          resolve(true);
        }
        iframeElement.onload = null;
      };
      window.removeEventListener('message', listenForChildPlayerReadyEvent);
      window.addEventListener('message', listenForChildPlayerReadyEvent);
      iframeElement.src = src;
      this.childPlayerIframe = iframeElement;
    });
  }

  /**
    * A function that's called when the Proquest iframe has finished loading. This function sends a postMessage to the child iframe to notify it that initialization has completed.
    *
    */
  handleLoadedIframe() {
    this.log('The iframe element has fully initialized! Its origin is:', this.postMessageOrigin);
    this.hasLoadedIframe = true;
    this.sendPostMessageToChildPlayer({ type: 'loadedIframe' });
    this.triggerQueuedEvents();
  }

  /**
    * A function that initializes the user interaction listeners, child player state object, the child player event listeners, and the iframe element itself before reporting to the parent VideoJS instance that everything is ready.
    *
    */
  async initializeChildPlayer() {
    if (!hasUserInteraction) this.initializeUserInteractionEvents();
    this.initChildPlayerState();
    this.initChildPlayerEvents();
    if (this.src()?.src) await this.loadIframe(this.src().src);
    this.triggerReady();
    this.log('The Iframe VideoJS tech has finished initializing.');
  }

  handleUserInteraction() {
    hasUserInteraction = true;
    this.hasUserInteraction = true;
  }

  /**
    * A function that listens for any click/scroll event. Browser security standards mandate that a page has received user interaction before videos inside iframes can be unmuted.
    *
    */
  initializeUserInteractionEvents() {
    document.removeEventListener('scroll', this.handleUserInteraction);
    document.removeEventListener('click', this.handleUserInteraction);
    document.addEventListener('scroll', this.handleUserInteraction, { once: true });
    document.addEventListener('click', this.handleUserInteraction, { once: true });
  }

  /**
    * A function that initializes the child player state, which is simply an object containing relevant playback information that we have received from the childPlayer.
    *
    */
  initChildPlayerState() {
    this._childPlayerState = {
      ended: false,
      playing: false,
      volume: this.options_.muted ? 0 : 1,
      playbackRate: 1,
      buffered: null,
      textTracks: [],
      progress: {
        seconds: 0,
        duration: 0,
      },
    };
  }

  /**
    * An object of all child player events that the Iframe tech listens to. These valeus can either be a custom event-handling function or `null`, indicating that they will be handled in the default manner (see the description for `listenForAllChildPlayerEvents()` for more details).
    *
    */
  childPlayerEventMap = {
    audioprocess: null,
    buffered: this.handleChildPlayerBufferedEvent,
    canplay: null,
    canplaythrough: null,
    complete: null,
    durationchange: this.handleChildPlayerDurationChangeEvent,
    emptied: null,
    ended: this.handleChildPlayerEndedEvent,
    error: this.handleChildPlayerErrorEvent,
    loadeddata: null,
    loadedmetadata: null,
    loadstart: null,
    pause: this.handleChildPlayerPauseEvent,
    play: this.handleChildPlayerPlayEvent,
    playing: null,
    progress: null,
    ratechange: this.handleChildPlayerRateChangeEvent,
    ready: this.handleChildPlayerReadyEvent,
    seeked: null,
    seeking: null,
    stalled: null,
    suspend: null,
    texttrackchange: this.handleChildPlayerTextTrackChangeEvent,
    timeupdate: this.handleChildPlayerTimeUpdateEvent,
    volumechange: this.handleChildPlayerVolumeChangeEvent,
    waiting: null,
  }

  /**
    * A function that returns the keys from the object that `childPlayerEventMap()` returns.
    *
    */
  childPlayerEventMapKeys() {
    return Object.keys(this.childPlayerEventMap);
  }

  /**
    * A function that initializes the event listener for the postMessage events listed in `childPlayerEventMap()`.
    *
    */
  initChildPlayerEvents() {
    const boundListenForAllChildPlayerEvents = this.listenForAllChildPlayerEvents.bind(this);
    window.removeEventListener('message', boundListenForAllChildPlayerEvents);
    window.addEventListener('message', boundListenForAllChildPlayerEvents);
  }

  /**
    * A function that listens to postMessages from the child player and handles them according to their key/value pair inside `childPlayerEventMap()`. If a key does not have a custom handling function (canplay, seeked, etc.), then the Iframe tech simply calls `this.trigger(<eventName>)` to pass the event upwards to the parent VideoJS instance.
    *
    * @param {Object} event - A postMessage event received from the childPlayer.
    */
  listenForAllChildPlayerEvents(event) {
    if (!this.eventBusEl_) return;
    let eventData;
    if (
      event?.data?.type === 'error'
      && event?.data?.value?.title?.length
    ) {
      // This is an error object that Proquest sends us. It doesn't match the other postMessage formats, which is why it has to be handled separately.
      const boundFunction = this.childPlayerEventMap.error.bind(this);
      return boundFunction(null, {
        message: event.data.value.title,
        isProquestError: true,
      });
    }
    try {
      eventData = JSON.parse(event?.data);
    } catch(e) {
      return;
    }
    if (
      eventData?.type === 'error'
      && eventData?.value?.title?.length
    ) {
      // This is an error object that Proquest sends us. It doesn't match the other postMessage formats, which is why it has to be handled separately.
      const boundFunction = this.childPlayerEventMap.error.bind(this);
      return boundFunction(null, {
        message: eventData.value.title,
        isProquestError: true,
      });
    }
    if (
      eventData?.source !== 'playpositChildPlayer'
      || eventData?.uuid !== this.uuid
      || !this.childPlayerEventMapKeys().includes(eventData?.type)
    ) return;
    if (event.source) this.postMessageSource = event.source;
    if (event.origin) this.postMessageOrigin = event.origin;
    const eventFunction = this.childPlayerEventMap[eventData.type];
    if (_.isFunction(eventFunction)) {
      const boundEventFunction = eventFunction.bind(this);
      boundEventFunction(eventData);
    } else {
      this.trigger(eventData.type);
    }
  }

  /**
    * A function that uses postMessages to send data down to the child player.
    *
    * @param {Object} event - An object that contains up to two properties. The first (`type`) is required and should be a string indicating the name of the event. The second (`arguments`) is optional and should be an array containing any arguments that the event might need. For example, you might pass an `arguments` value of `[60]` alongside an event with a type of `currentTime` to tell the child player to seek to 60 seconds.
    */
  sendPostMessageToChildPlayer(event = {}) {
    this.log('sendPostMessageToChildPlayer() 1');
    if (
      !this.postMessageSource
      || !this.postMessageOrigin
      || !this.childPlayerIframe?.contentWindow
    ) return;
    if (!this.hasLoadedIframe) {
      this.queuedEvents.push(event);
      return;
    }
    this.log('sendPostMessageToChildPlayer() 2', event.type);
    this.childPlayerIframe.contentWindow.postMessage(JSON.stringify({
      source: 'playpositParentPlayer',
      uuid: this.uuid,
      ...event,
    }), this.postMessageOrigin);
  }

  /**
    * A function that triggers queued events that were first received when the Iframe tech was still initializing.
    *
    */
  triggerQueuedEvents() {
    for (const event of this.queuedEvents) {
      this.sendPostMessageToChildPlayer(event);
    }
    this.queuedEvents = [];
  }

  /**
    * A function that handles the child player's "buffered" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage. This arguments array contains numbers representing the current portion of the video that has buffered within the child player.
    */
  handleChildPlayerBufferedEvent(eventData) {
    for (const bufferArray of eventData.arguments) {
      this.bufferedTimeRange = videojs.createTimeRange(bufferArray[0], bufferArray[1]);
    }
  }

  /**
    * A function that handles the child player's "durationchange" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage. This arguments array contains the duration of the video as gathered by the child player.
    */
  handleChildPlayerDurationChangeEvent(eventData) {
    this.log('handleChildPlayerDurationChangeEvent()', eventData.arguments[0]);
    ([this._childPlayerState.progress.duration] = eventData.arguments);
    this.trigger('durationchange');
  }

  /**
    * A function that handles the child player's "ended" event.
    *
    */
  handleChildPlayerEndedEvent() {
    this.log('handleChildPlayerEndedEvent()');
    this._childPlayerState.playing = false;
    this._childPlayerState.ended = true;
    this.trigger('ended');
  }

  /**
    * A function that handles the child player's "error" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage. This arguments array contains the error that the child player has run into.
    */
  handleChildPlayerErrorEvent(eventData, errorObject) {
    if (eventData) {
      ([this.childPlayerError] = eventData.arguments);
    } else {
      this.childPlayerError = errorObject;
    }
    this.trigger('error');
  }

  /**
    * A function that handles the child player's "pause" event.
    *
    */
  handleChildPlayerPauseEvent() {
    this.log('handleChildPlayerPauseEvent()');
    this._childPlayerState.playing = false;
    this.trigger('canplay');
    this.trigger('pause');
  }

  /**
    * A function that handles the child player's "play" event.
    *
    */
  handleChildPlayerPlayEvent() {
    this.log('handleChildPlayerPlayEvent()');
    this._childPlayerState.playing = true;
    this._childPlayerState.ended = false;
    if (!this.hasTriggeredFirstPlay) {
      this.trigger('firstplay');
      this.hasTriggeredFirstPlay = true;
    }
    this.trigger('play');
  }

  /**
    * A function that handles the child player's "ratechange" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage. This arguments array contains the playback rate of the child player.
    */
  handleChildPlayerRateChangeEvent(eventData) {
    this.log('handleChildPlayerRateChangeEvent()', eventData.arguments[0]);
    ([this._childPlayerState.playbackRate] = eventData.arguments);
    this.trigger('ratechange');
  }

  /**
    * A function that handles the child player's "ready" event.
    *
    */
  handleChildPlayerReadyEvent() {
    this.log('handleChildPlayerReadyEvent()', this.options_.muted);
    if (this.options_.muted) {
      this.sendPostMessageToChildPlayer({
        type: 'volume',
        arguments: [0],
      });
    }
    this.trigger('ready');
    this.trigger('canplay');
    if (this.playWhenReady) {
      this.playWhenReady = false;
      this.play();
    }
  }

  /**
    * A function that handles the child player's "texttrackchange" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage. This arguments array contains all of the caption tracks gleamed from the child player.
    */
  handleChildPlayerTextTrackChangeEvent(eventData) {
    this._childPlayerState.textTracks = eventData.arguments;
    this.trigger('texttrackchange', eventData.arguments);
    this.player_.trigger('texttrackchange', eventData.arguments);
  }

  /**
    * A function that handles the child player's "timeupdate" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage.
    */
  handleChildPlayerTimeUpdateEvent(eventData) {
    this.log('handleChildPlayerTimeUpdateEvent()', eventData.arguments[0]);
    ([this._childPlayerState.progress.seconds] = eventData.arguments);
    this.trigger('timeupdate');
  }

  /**
    * A function that handles the child player's "buffered" event.
    *
    * @param {Object} eventData - The parsed JSON string of an event received from a child player postMessage.
    */
  handleChildPlayerVolumeChangeEvent(eventData) {
    this.log('handleChildPlayerVolumeChangeEvent()', eventData.arguments[0]);
    ([this._childPlayerState.volume] = eventData.arguments);
    this.trigger('volumechange');
  }

  /**
    * An asynchronous function that only resolves after `this._childPlayerState` has been instantiated.
    *
    */
  async waitForChildStateInitialization() {
    return new Promise((resolve) => {
      if (!_.isEmpty(this._childPlayerState)) return resolve(this._childPlayerState);
      const childStateInterval = setInterval(() => {
        if (!_.isEmpty(this._childPlayerState)) {
          clearInterval(childStateInterval);
          return resolve(this._childPlayerState);
        }
      }, 50);
    });
  }

  /**
    * A function that determines whether or not the VideoJS player controls are enabled.
    *
    * @return {Boolean} this.options_.controls - A boolean that, if true, means that the VideoJS player controls are enabled.
    */
  controls() {
    return this.options_.controls;
  }

  /**
    * A function that determines whether or not fullscreen videos are supported.
    *
    * @return {Boolean} - true
    */
  supportsFullScreen() {
    return this.fullscreen;
  }

  /**
    * A function that returns the current video src.
    *
    * @return {String} this.source.src - The child player iframe's src URL.
    */
  currentSrc() {
    return this.source?.src;
  }

  /**
    * A function that returns the volume value currently stored in the child player state.
    *
    * @return {Integer} this._childPlayerState.volume - The volume of the player. Ranges from 0 to 1.
    */
  volume() {
    this.log('volume()', this._childPlayerState.volume);
    return this._childPlayerState.volume;
  }

  /**
    * A function that sets the volume by sending a postMessage to the child player.
    *
    * @param {Integer} volume - The volume of the player. Ranges from 0 to 1.
    */
  async setVolume(volume) {
    this.log('setVolume()', volume);
    await this.waitForChildStateInitialization();
    this._childPlayerState.volume = volume;
    this.sendPostMessageToChildPlayer({
      type: 'volume',
      arguments: [volume],
    });
  }

  /**
    * A function that returns an array of caption tracks gleamed from the child player.
    *
    * @return {Array} An array of the child player's caption text tracks.
    */
  childTextTracks() {
    return this._childPlayerState.textTracks;
  }

  /**
    * A function that returns the duration of the video, collected from the child player.
    *
    * @return {Integer} An integer representing the length of the child player's video in seconds.
    */
  duration() {
    this.log('duration()', this?._childPlayerState?.progress?.duration);
    return this?._childPlayerState?.progress?.duration;
  }

  /**
    * A function that returns the error that the child player has run into (if applicable.)
    *
    * @return {Object} A VideoJS error object received from the child player.
    */
  error() {
    this.log('error()', this.childPlayerError);
    if (!this.childPlayerError) return null;
    return {
      ...this.childPlayerError,
      message: this.childPlayerError?.value?.title || this.childPlayerError?.message,
    };
  }

  /**
    * A function that returns an array of integers that represents the buffered portions of the video.
    *
    * @return {Array} An array of integers that represents the buffered portions of the video.
    */
  buffered() {
    return this.bufferedTimeRange;
  }

  /**
    * A function that returns whether or not the video is currently buffering.
    *
    * @return {Boolean} If `true`, then the video is buffering.
    */
  buffering() {
    return this.isBuffering;
  }

  /**
    * A function that returns whether or not the video is currently paused.
    *
    * @return {Boolean} If `true`, then the video is paused.
    */
  paused() {
    this.log('paused()', !this._childPlayerState.playing);
    return !this._childPlayerState.playing;
  }

  /**
    * A function that sends a postMessage to the child player, instructing it to pause.
    *
    */
  pause() {
    this.log('pause()');
    this.sendPostMessageToChildPlayer({ type: 'pause' });
  }

  /**
    * A function that sends a postMessage to the child player, instructing it to play.
    *
    * Since the childPlayer is within an iframe, we can't fire off a play event until the user has interacted with the page. We use `initializeUserInteractionEvents()` to listen for either a click event or a scroll event, either of which serves as user interaction for this purpose. Without the `setTimeout()` in the following code, the play event is triggered BEFORE the user interaction click so we need `setTimeout()` here in order to ensure that the user interaction click is registered before the play event.
    */
  play() {
    setTimeout(() => {
      this.log('play() 1');
      if (
        !this.hasLoadedIframe
        || !hasUserInteraction
        || this._childPlayerState.playing
      ) {
        if (!this.hasLoadedIframe) this.playWhenReady = true;
        return;
      }
      this.log('play() 2');
      this.sendPostMessageToChildPlayer({ type: 'play' });
    });
  }

  /**
    * A getter/setter function that sets the `muted` property of the video (if an argument is provided) or returns the current `muted` property of the video (if not).
    *
    * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video. If `undefined`, return the current `this._childPlayerState.muted` value.
    *
    * @return {Boolean} - Represents whether the video is currently muted or not.
    */
  muted(isMuted) {
    this.log('muted()', this._childPlayerState.volume === 0);
    if ([null, undefined].includes(isMuted)) return this._childPlayerState.volume === 0;
    return this.setMuted(isMuted);
  }

  /**
    * A function that sends a postMessage to the child player, instructing it to change its volume to either 0 or 1 based on whether the video should be muted or not.
    *
    * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video. If `undefined`, return the current `this._childPlayerState.muted` value.
    *
    * @return {Boolean} - Represents whether the video is currently muted or not.
    */
  setMuted(isMuted) {
    this.log('setMuted()', isMuted);
    const volume = isMuted ? 0 : 1;
    this.sendPostMessageToChildPlayer({
      type: 'volume',
      arguments: [volume],
    });
    return !!volume;
  }

  /**
    * A function that returns whether or not the video has ended.
    *
    * @return {Boolean} - Represents whether the video has ended or not.
    */
  ended() {
    this.log('ended()', this._childPlayerState.ended);
    return this._childPlayerState.ended;
  }

  /**
    * A function that returns the child player's playback rate.
    *
    * @return {Integer} - A number representing the child player's current playback rate.
    */
  playbackRate() {
    this.log('playbackRate()', this._childPlayerState.playbackRate);
    return this._childPlayerState.playbackRate;
  }

  /**
    * A function that sends a postMessage to the child player, instructing it to change its playback rate to the provided integer.
    *
    * @param {Integer} playbackRate - The desired playback rate for the video.
    *
    * @return {Integer} - The desired playback rate of the video.
    */
  setPlaybackRate(playbackRate) {
    this.log('setPlaybackRate()', playbackRate);
    this.sendPostMessageToChildPlayer({
      type: 'playbackRate',
      arguments: [playbackRate],
    });
    return playbackRate;
  }

  /**
    * A getter/setter function that sets the `src` property of the video (if an argument is provided) or returns the current `src` property of the video (if not).
    *
    * @param {String} src - A string representing the URL of the Proquest video.
    *
    * @return {Object} - An object containing the video source (String) as well as its type (String).
    */
  src(src) {
    this.log('src()', src);
    if (src) return this.setSrc({ src });
    return this.source;
  }

  /**
    * A function that sets the video's source before initializing the child player.
    *
    * @param {Object} source - An object containing the video source (String) as well as its type (String).
    *
    * @return {Object} - An object containing the video source (String) as well as its type (String).
    */
  setSrc(source = {}) {
    this.log('setSrc()', source);
    if (!source?.src) return;
    delete this.childPlayerError;
    this.log(source.src, this.src()?.src);
    if (source.src === this.src()?.src) return;
    let srcUrl = `${this.pqOrigin}/pp-embed/${source.src}?playposit&uuid=${this.uuid}&token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE2NjQzNzgzMjksIm5iZiI6MTY2NDM3ODMyOSwianRpIjoiNzQ4YzU2ODktZWZjNC00NjJkLTk3OTUtOGVlNWE0ZTM3MjBlIiwiZXhwIjoyNTI4Mzc4MzI5LCJpZGVudGl0eSI6eyJpbnN0aXR1dGlvbklkIjoiMzQ0MTgiLCJpbnN0aXR1dGlvbk5hbWUiOiJQbGF5UG9zaXQgRGV2ZWxvcG1lbnQgQWNjb3VudCIsImFjY291bnRJZCI6IjIyMTAyMyIsInVzYWdlR3JvdXBJZCI6IjE0MjYzMTciLCJjb3VudHJ5Q29kZSI6IlVTIiwiYXV0b0NvdW50cnkiOiIwIiwiaXBDb3VudHJ5Q29kZSI6IlVTIn0sImZyZXNoIjpmYWxzZSwidHlwZSI6ImFjY2VzcyJ9.AA3Wbed-YW9PM-8gWjZSKT0tt-j8dgxKqDblB-UyrXc${this.ppWrapper ? `&ppWrapper=${this.ppWrapper}` : ''}`;
    if (this.proquestVideoUrl?.length) {
      srcUrl = `${this.proquestVideoUrl}${this.proquestVideoUrl.includes('?') ? '&' : '?'}uuid=${this.uuid}`;
    }
    this.source = {
      src: srcUrl,
      slug: source.src,
      type: source.type,
    };
    this.initializeChildPlayer();

    if (
      this.options_.autoplay
      && !_isOnMobile
    ) {
      if (this.isReady_) this.play();
      else this.playWhenReady = true;
    }
    return this.source;
  }

  /**
    * A getter/setter function that sets the `currentTime` property of the video (if an argument is provided) or returns the current `currentTime` property of the video (if not).
    *
    * @param {Integer} time - A number representing the desired time for the video to seek to.
    *
    * @return {Integer} - A number representing the desired time for the video to seek to.
    */
  currentTime(time) {
    if (
      this.isBuffering
      && parseFloat(this._childPlayerState?.progress?.seconds?.toFixed(2)) === parseFloat(this.seekTime?.toFixed(2))
    ) {
      this.seekTime = null;
      this.trigger('seeked');
      this.isBuffering = false;
    }
    if (!_.isFinite(time)) return this._childPlayerState.progress.seconds;
    return this.setCurrentTime(time);
  }

  /**
    * A function that sends a postMessage to the child player, instructing it to seek to the provided time.
    *
    * @param {Integer} time - The desired time for the video to seek to.
    *
    * @return {Integer} - The desired time for the video to seek to.
    */
  setCurrentTime(time) {
    if (time === this.seekTime) return;
    this.log('setCurrentTime()', time);
    this.seekTime = time;
    this.isBuffering = true;
    this.trigger('seeking');
    this.sendPostMessageToChildPlayer({
      type: 'currentTime',
      arguments: [time],
    });
    return time;
  }

  /**
    * A function that returns whether or not the child player's video is currently playing.
    *
    * @return {Boolean} - If `true`, the child player's video is playing. If `false`, it is not playing.
    */
  async playing() {
    this.log('playing()', this._childPlayerState.playing);
    if (this._childPlayerState.playing) this.isBuffering = false;
    return this._childPlayerState.playing;
  }

  /**
    * A function that's called whenever the parent player is destroyed so that the Iframe tech's main element can be removed from the DOM.
    *
    */
  dispose() {
    if (
      this.el_
      && this.el_.querySelector(`#videojs-child-player-iframe-${this.uuid}`)
    ) {
      this.el_.querySelector(`#videojs-child-player-iframe-${this.uuid}`).remove();
    }
  }
}

/**
  * A function that determines whether or not the Iframe tech is supported.
  *
  * @return {Boolean} - true
  */
Iframe.isSupported = function isSupported() {
  return true;
};

/**
  * A function that determines whether or not the Iframe tech supports fullscreen.
  *
  * @return {Boolean} - true
  */
Iframe.supportsFullScreen = function supportsFullScreen() {
  return true;
};

/**
  * A function that determines whether or not the provided video src matches the expected video pattern, which is a typical URL slug (e.g. "bbc-proms-2011-brahms-night").
  *
  * @return {Boolean} - If `true`, then then provided src matches the expected pattern. If `false`, it does not.
  */
Iframe.srcMatchesExpectedPattern = function srcMatchesExpectedPattern(src) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(src);
};

/**
  * A function that determines whether or not the provided video type matches the Iframe tech's supported video type.
  *
  * @return {Boolean} - If `true`, then the video type is supported. If `false`, then it is not.
  */
Iframe.canPlayType = function canPlayType(type) {
  return type === 'video/proquest';
};

/**
  * A function that determines whether or not the provided video src matches the expected video pattern AND whether or not the provided video type matches the Iframe tech's supported video type.
  *
  * @return {Boolean} - If `true`, then the video src and type are supported. If `false`, then they are not.
  */
Iframe.canPlaySource = function canPlaySource({
  src,
  type,
} = {}) {
  return (
    Iframe.srcMatchesExpectedPattern(src)
    && Iframe.canPlayType(type)
  );
};

Tech.registerTech('Iframe', Iframe);
export default Iframe;